import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalTransactionService from '../services/portalTransactionService';
import { RecentListRowsSkeleton } from './skeletons';
import { TransactionRow } from './TransactionRow';

/**
 * Account-home recent transactions (PD-14080): the latest few payments /
 * credits / voids with a link to the full Transactions page — the
 * recent-activity pattern customers asked for.
 */
export function RecentTransactionsCard({
	title,
}: {
	title?: string;
} = {}): ReactElement | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const display = useDisplaySettings();
	const { token, activeClientId, availableClients } = usePortalAuth();

	const { data, isLoading } = useQuery({
		// Key on the active client so the card refetches on an account switch.
		queryKey: ['portal-transactions-recent', activeClientId],
		queryFn: () =>
			portalTransactionService.getTransactions(token ?? '', {
				page: 1,
				limit: 3,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	// PD-14691: gate on the transactions surface's own flag (independently
	// toggleable per portal since PD-14675; hide-pricing forces it off too).
	// Defensive — the dashboard dispatch already skips mounting this card.
	if (!display.canViewTransactions) {
		return null;
	}

	// Skeleton-first (PD-14692): real header + shaped rows. isLoading is
	// false while the query is disabled, so anon visitors fall through.
	if (isLoading) {
		return (
			<div>
				<div className="flex items-center justify-between mb-2">
					<span className="text-sm font-semibold text-default">
						{t(title ?? 'Recent Transactions')}
					</span>
				</div>
				<RecentListRowsSkeleton />
			</div>
		);
	}

	const transactions = data?.data ?? [];
	if (transactions.length === 0) {
		return null;
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-semibold text-default">
					{t(title ?? 'Recent Transactions')}
				</span>
				<button
					type="button"
					onClick={() => {
						void navigate('/transactions');
					}}
					className="text-xs text-portal-accent hover:underline"
				>
					{t('View all')}
				</button>
			</div>
			<div className="flex flex-col gap-2">
				{transactions.map((txn) => (
					<TransactionRow
						key={txn.id}
						txn={txn}
						showClient={availableClients.length > 1}
					/>
				))}
			</div>
		</div>
	);
}
